import React, { useEffect, useRef, useState, memo } from 'react';
import ReactEChartsCore from '../EChart';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from '../../../providers/AppProvider';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
  DataZoomComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { Card } from 'react-bootstrap';
import axios from 'axios';
import { FallingLines } from 'react-loader-spinner';
import { isLiveTimeRange } from '../utils/chartUtils';
import { INFLUX_API } from '../../../config/api';
// Use the imported components
echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent,
  DataZoomComponent
]);

const getDefaultOptions = (
  theme,
  getThemeColor,
  actualDiameter,
  diameterParameter,
  title
) => {
  const sortedActualDiameter = actualDiameter.sort(
    (a, b) => new Date(a[0]) - new Date(b[0])
  );
  const sortedDiameterParameter = diameterParameter.sort(
    (a, b) => new Date(a[0]) - new Date(b[0])
  );

  // Y-axis scaling — fall back to a small auto range when data is empty/incomplete.
  const values = sortedActualDiameter
    .map(item => item[1])
    .filter(v => typeof v === 'number' && isFinite(v));
  let dynamicMin, dynamicMax;
  if (values.length === 0) {
    dynamicMin = undefined; // echarts auto-scales
    dynamicMax = undefined;
  } else {
    const minValue = Math.min(...values);
    const maxValue = Math.max(...values);
    const buffer = Math.max((maxValue - minValue) * 0.05, 0.01);
    dynamicMin = (minValue - buffer).toFixed(3);
    dynamicMax = (maxValue + buffer).toFixed(3);
  }

  return {
    color: [getThemeColor('primary'), getThemeColor('info')],
    title: {
      text: title
    },
    tooltip: {
      trigger: 'axis',
      padding: 10,
      backgroundColor: getThemeColor('gray-100'),
      borderColor: getThemeColor('gray-300'),
      textStyle: { color: getThemeColor('dark') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'line',
        axis: 'x'
      },
      formatter: function (params) {
        if (!params || params.length === 0) return '';
        const safe = i =>
          params[i] && params[i].value && typeof params[i].value[1] === 'number'
            ? params[i].value[1]
            : null;
        const tRaw = params[0]?.value?.[0];
        const time = tRaw ? dayjs(tRaw).format('HH:mm:ss') : '—';
        const actual = safe(0);
        const parameter = safe(1);
        const fmt = v => (v == null ? '—' : v.toFixed(3));
        const diff =
          actual != null && parameter != null
            ? (actual - parameter).toFixed(3)
            : '—';
        return `Time: ${time}<br/>Actual Diameter: ${fmt(actual)}<br/>Parameter: ${fmt(parameter)}<br/>Difference: ${diff}`;
      }
    },
    xAxis: {
      type: 'time',
      boundaryGap: false,
      axisLabel: {
        formatter: value => dayjs(value).format('HH:mm'),
        interval: 'auto',
        margin: 10
      },
      splitLine: {
        show: false
      }
    },
    yAxis: {
      position: 'right',
      axisPointer: { type: 'none' },
      axisTick: { show: false },
      splitLine: {
        show: false
      },
      axisLine: { show: false },
      axisLabel: {
        show: true,
        margin: 15
      },
      min: dynamicMin, // Dynamically set min with buffer
      max: dynamicMax // Dynamically set max with buffer
    },
    series: [
      {
        type: 'line',
        data: sortedActualDiameter,
        showSymbol: false,
        symbol: 'circle',
        smooth: true,
        lineStyle: {
          width: 2, // Make actual diameter line thicker
          color: getThemeColor('primary')
        }
      },
      {
        type: 'line',
        data: sortedDiameterParameter,
        lineStyle: {
          type: 'dashed',
          width: 1,
          color: getThemeColor('info')
        },
        showSymbol: false,
        symbol: 'circle'
      }
    ],
    grid: {
      right: 40,
      left: 20,
      bottom: '20px',
      top: '10%',
      containLabel: true
    },
    animation: true,
    dataZoom: [
      {
        type: 'inside',
        xAxisIndex: 0,
        start: 0,
        end: 100
      }
    ]
  };
};
const MachineDiameterChart = ({
  bucket,
  machineName,
  fields,
  startTime,
  endTime,
  title = 'Diameter fluctuation'
}) => {
  const chartRef = useRef(null);
  const [actualDiameter, setActualDiameter] = useState([]);
  const [diameterParameter, setDiameterParameter] = useState([]);
  const [loading, setLoading] = useState(true);
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${INFLUX_API}/api/influx/${machineName}/fields?fields=${fields}&start=${startTime}&end=${endTime}&bucket=${bucket}`
      );
      const keys = Object.keys(response.data);

      // Convention: "set" tags carry "SET" / "Set" in the name; everything else with
      // "act" / "actual" is the live reading. Covers L75 (ActualDiameter/DiameterSetValue),
      // old L120 (SET_DIA/DIA_ACT_VALUE), tandem (FILLING_DIA_ACT_VALUE, JACKETING_DIA_ACT_VALUE etc.).
      const isActual = k => {
        const lower = k.toLowerCase();
        return (
          (lower.includes('actual') || lower.includes('act')) &&
          !lower.includes('set')
        );
      };
      keys.forEach(key => {
        if (isActual(key)) {
          setActualDiameter(response.data[key]);
        } else {
          setDiameterParameter(response.data[key]);
        }
      });
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const intervalId = isLiveTimeRange(endTime) ? setInterval(fetchData, 60000) : null;
    return () => clearInterval(intervalId);
  }, [machineName, fields, startTime, endTime]);

  if (loading) {
    return (
      <Card className="h-0">
        <Card.Body style={{ height: '316px', width: '100%' }}>
          <div style={styles.spinnerContainer}>
            <FallingLines
              color={theme === 'dark' ? 'white' : '#3874ff'}
              width="100"
              visible={true}
              ariaLabel="falling-circles-loading"
            />
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <Card.Body>
        <ReactEChartsCore
          ref={chartRef}
          echarts={echarts}
          option={getDefaultOptions(
            theme,
            getThemeColor,
            actualDiameter,
            diameterParameter,
            title
          )}
          style={{ height: '316px', width: '100%' }}
        />
      </Card.Body>
    </Card>
  );
};

const styles = {
  spinnerContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: '100%'
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px'
  },
  title: {
    fontSize: '16px',
    fontWeight: 'bold',
    // minWidth lets a long title wrap instead of shoving the resolution
    // picker out of the card: a flex item will not shrink below its
    // content width until it is told it may.
    flex: '1 1 auto',
    minWidth: 0,
    lineHeight: 1.25,
    paddingRight: '8px'
  }
};

export default memo(MachineDiameterChart);
